import { Box, FormHelp } from '@zillow/constellation-10';
import type { ReactNode } from 'react';
import { css } from '@/styled-system/css';

// Per Panda/Bamboo guidance for shipped libraries (Kris Ellery, #engineering
// channel 2026-04-13): always precompute styles with `css()` / `css.raw()` /
// `cva()` at module scope, then pass the result. Inline object literals passed
// to the `css` prop are dropped when the library is compiled to `dist/` because
// Panda's static analysis can't extract them from compiled JSX output.
// See https://github.com/chakra-ui/panda/issues/3509 for the upstream bug.
const helperTextClass = css({ flex: 1, minWidth: 0 });
const counterClass = css({
  whiteSpace: 'nowrap',
  flexShrink: 0,
});
const counterOnlyClass = css({
  whiteSpace: 'nowrap',
  flexShrink: 0,
  marginLeft: 'auto',
});
const contentWrapperStyles = css.raw({
  display: 'flex',
  gap: 'tighter',
  alignItems: 'flex-start',
  flex: 1,
});

export type CharacterCountFormHelpProps = {
  /** Current length of the input value. Drives visibility and error state. */
  currentLength: number;
  /**
   * Maximum allowed length (soft limit).
   * Do NOT pass this as `maxLength` to the Input/Textarea — users must be able to type past it.
   */
  maxLength: number;
  /**
   * Whether the input is currently focused.
   * Controls whether the counter is visible below the limit.
   * Owned by the consumer (wire it up with `onFocus` / `onBlur` on the input).
   */
  isFocused: boolean;
  /**
   * Main helper text shown on the left, while within the character limit.
   * Omit when the field doesn't need helper text — the counter will flow right.
   * `ReactNode` so consumers can embed inline formatting (<strong>, links, etc.).
   */
  helperText?: ReactNode;
  /**
   * Field label used to build the default over-limit error message.
   * The first character is auto-capitalized, so both `"email"` and `"Email"` work.
   * Required when you don't provide `errorText` explicitly.
   */
  label?: string;
  /**
   * Override the over-limit error text.
   * Use this when the label doesn't read naturally in the default phrasing
   * (for example, "Customize your message must be 500 characters or fewer").
   */
  errorText?: string;
};

/**
 * CharacterCountFormHelp — drop-in `FormHelp` content for a soft-limit character counter.
 *
 * Renders:
 * - Helper text on the left (while within the limit), replaced by an error message when over
 * - Character counter on the right (visible when focused or over the limit)
 * - Nothing at all when unfocused, within the limit, and no helper text
 *
 * Pair with the CharacterCount pattern (`modules/visual-cohesion/src/patterns/CharacterCount.stories.tsx`)
 * for the surrounding structure — consumers still own `FormField`, the `Input`/`Textarea`,
 * and the value/focus state. Set `error` on the parent `FormField` (not here) so the
 * `aria-invalid` state cascades correctly to the input.
 *
 * @example
 * <FormField
 *   label={<Label>Description</Label>}
 *   error={value.length > MAX}
 *   description={
 *     <CharacterCountFormHelp
 *       currentLength={value.length}
 *       maxLength={MAX}
 *       isFocused={isFocused}
 *       helperText="Describe the issue in detail"
 *       label="Description"
 *     />
 *   }
 *   control={<Textarea ... />}
 * />
 */
export function CharacterCountFormHelp({
  currentLength,
  maxLength,
  isFocused,
  helperText,
  label,
  errorText,
}: CharacterCountFormHelpProps) {
  const isOverLimit = currentLength > maxLength;
  const showCounter = isFocused || isOverLimit;
  const hasHelperText = helperText !== undefined && helperText !== '';

  if (!showCounter && !hasHelperText) {
    return null;
  }

  const resolvedErrorText = errorText ?? buildDefaultErrorText(label, maxLength);

  if (hasHelperText) {
    return (
      <FormHelp>
        <Box css={contentWrapperStyles}>
          <span className={helperTextClass}>{isOverLimit ? resolvedErrorText : helperText}</span>
          {showCounter && (
            <span className={counterClass}>
              {currentLength.toLocaleString()}/{maxLength.toLocaleString()} characters
            </span>
          )}
        </Box>
      </FormHelp>
    );
  }

  // Counter-only layout (no helper text). FormHelp is only rendered when it
  // actually has something to say — on focus or when over the limit.
  return (
    <FormHelp>
      <Box css={contentWrapperStyles}>
        {isOverLimit && <span className={helperTextClass}>{resolvedErrorText}</span>}
        <span className={counterOnlyClass}>
          {currentLength.toLocaleString()}/{maxLength.toLocaleString()} characters
        </span>
      </Box>
    </FormHelp>
  );
}

function buildDefaultErrorText(label: string | undefined, maxLength: number): string {
  // Capitalize only the first character so "email" → "Email" but "SSN" stays "SSN".
  const resolvedLabel = label && label.length > 0 ? label.charAt(0).toUpperCase() + label.slice(1) : 'Text';
  return `${resolvedLabel} must be ${maxLength.toLocaleString()} characters or fewer`;
}
